import { Component, input, output } from '@angular/core';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import { HlmEmptyImports } from '@spartan-ng/helm/empty';
import type { ManualSearchResult } from './manual.graphql';

/**
 * Results of a manual search, shared by the help panel and the `/manual` page: the searching
 * status, the empty state or one button per result. Opening a result is the host's decision, so a
 * click is only emitted as `resultOpen`.
 */
@Component({
  selector: 'au-manual-search-results',
  imports: [TranslocoDirective, HlmEmptyImports],
  providers: [provideTranslocoScope('manual')],
  template: `
    <ng-container *transloco="let t; scope: 'manual'">
      @if (searching()) {
        <p
          role="status"
          class="text-muted-foreground text-sm"
        >
          {{ t('manual.search.Searching') }}
        </p>
      } @else if (results(); as results) {
        @if (results.length === 0) {
          <hlm-empty data-testid="manual-search-empty">
            <hlm-empty-header>
              <h3 hlmEmptyTitle>{{ t('manual.search.EmptyTitle') }}</h3>
              <p hlmEmptyDescription>{{ t('manual.search.EmptyDescription') }}</p>
            </hlm-empty-header>
          </hlm-empty>
        } @else {
          <ul class="flex flex-col gap-2">
            @for (result of results; track result.id) {
              <li>
                <button
                  data-testid="manual-search-result"
                  type="button"
                  class="hover:bg-accent focus-visible:ring-ring flex w-full flex-col gap-1 rounded-md border p-3 text-start outline-none focus-visible:ring-2"
                  (click)="resultOpen.emit(result)"
                >
                  <span class="text-sm font-medium">{{ result.title }}</span>
                  <span class="text-muted-foreground text-sm">{{ result.excerpt }}</span>
                </button>
              </li>
            }
          </ul>
        }
      }
    </ng-container>
  `,
})
export class ManualSearchResults {
  readonly searching = input(false);
  /** `null` when no search is on screen. */
  readonly results = input<ManualSearchResult[] | null>(null);
  readonly resultOpen = output<ManualSearchResult>();
}
